/**
 * Site Settings V2 — "Solar" section: solar hardware specs, install
 * milestones, and permitting fields. Only rendered when the site
 * supports solar.
 *
 * Contains:
 * - SolarSection (default export)
 */

import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  SettingsFieldGroup,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTextField,
} from '@sophocles/views/settings/components';

export default function SolarSection() {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-solar"
      icon={<WbSunnyIcon />}
      title={t('siteSettings.v2.solar', 'Solar')}
      description={t(
        'siteSettings.v2.solarDescription',
        'Solar hardware, install milestones, and permitting for this site.',
      )}
    >
      <Stack spacing={3}>
        <SettingsSubsection title={t('siteSettings.solar.basic', 'Basic Options')} bordered>
          <Stack spacing={2}>
            <SettingsSwitchField
              name="solar.solar_active"
              label={t('siteSettings.solar.active', 'Active Solar Site')}
            />
            <SettingsSwitchField
              name="solar.exclude_from_solar_report"
              label={t('siteSettings.solar.excludeFromReport', 'Exclude From Solar Report')}
            />
            <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
              <SettingsTextField
                name="solar.nominal_size"
                label={t('siteSettings.solar.nominalSize', 'Nominal Size')}
              />
              <SettingsTextField
                name="solar.number_of_faces"
                label={t('siteSettings.solar.numberOfFaces', 'Number of Faces')}
                type="number"
              />
              <SettingsTextField
                name="solar.solar_generator_voltage"
                label={t('siteSettings.solar.meterVoltage', 'Solar Meter Voltage')}
                type="number"
              />
              <SettingsTextField
                name="solar.solar_orientation"
                label={t('siteSettings.solar.orientation', 'Orientation')}
              />
              <SettingsTextField
                name="solar.configuration"
                label={t('siteSettings.solar.configuration', 'Configuration')}
              />
              <SettingsTextField
                name="solar.solar_panel_tilt"
                label={t('siteSettings.solar.tilt', 'Solar Panel Tilt')}
                type="number"
              />
            </SettingsFieldGroup>
            <SettingsSwitchField
              name="solar.obstructions_to_solar"
              label={t('siteSettings.solar.obstructions', 'Any Obstructions')}
            />
            <SettingsSwitchField
              name="solar.tree_trimming_required"
              label={t('siteSettings.solar.treeTrimming', 'Tree Trimming Required')}
            />
          </Stack>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.installed', 'Installed Components')} bordered>
          <Stack spacing={2}>
            <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
              <SettingsTextField
                name="solar.solar_panel_manufacturer"
                label={t('siteSettings.solar.panelManufacturer', 'Solar Panel Manufacturer')}
              />
              <SettingsTextField
                name="solar.solar_panel_model_number"
                label={t('siteSettings.solar.panelModel', 'Solar Panel Model #')}
              />
              <SettingsTextField
                name="solar.solar_panel_wattage"
                label={t('siteSettings.solar.panelWattage', 'Panel Wattage')}
                type="number"
              />
              <SettingsTextField
                name="solar.solar_array_serial"
                label={t('siteSettings.solar.arraySerial', 'Array Serial')}
              />
              <SettingsTextField
                name="solar.panels_per_array"
                label={t('siteSettings.solar.panelsPerArray', 'Panels Per Array')}
                type="number"
              />
              <SettingsTextField
                name="solar.number_of_arrays_installed"
                label={t('siteSettings.solar.arraysInstalled', 'Arrays Installed')}
                type="number"
              />
              <SettingsTextField
                name="solar.total_system_wattage"
                label={t('siteSettings.solar.totalWattage', 'Total System Wattage')}
                type="number"
              />
              <SettingsTextField
                name="solar.system_voltage"
                label={t('siteSettings.solar.systemVoltage', 'System Voltage')}
                type="number"
              />
              <SettingsTextField
                name="solar.quantity_of_inverters_installed"
                label={t('siteSettings.solar.inverterQty', 'Inverters Installed')}
                type="number"
              />
              <SettingsTextField
                name="solar.inverter_manufacturer"
                label={t('siteSettings.solar.inverterManufacturer', 'Inverter Manufacturer')}
              />
              <SettingsTextField
                name="solar.inverter_model_number"
                label={t('siteSettings.solar.inverterModel', 'Inverter Model #')}
              />
              <SettingsTextField
                name="solar.inverter_serial_number"
                label={t('siteSettings.solar.inverterSerial', 'Inverter Serial #1')}
              />
              <SettingsTextField
                name="solar.inverter_serial_number_2"
                label={t('siteSettings.solar.inverterSerial2', 'Inverter Serial #2')}
              />
              <SettingsTextField
                name="solar.inverter_serial_number_3"
                label={t('siteSettings.solar.inverterSerial3', 'Inverter Serial #3')}
              />
              <SettingsTextField
                name="solar.lighting_manufacturer"
                label={t('siteSettings.solar.lightingManufacturer', 'Lighting Manufacturer')}
              />
              <SettingsTextField
                name="solar.lighting_model_number"
                label={t('siteSettings.solar.lightingModel', 'Lighting Model #')}
              />
              <SettingsTextField
                name="solar.total_number_lights_installed"
                label={t('siteSettings.solar.lightsInstalled', 'Lights Installed')}
                type="number"
              />
              <SettingsTextField
                name="solar.lighting_design_wattage"
                label={t('siteSettings.solar.lightingWattage', 'Lighting Design Wattage')}
                type="number"
              />
              <SettingsTextField
                name="solar.lighting_controller_manufacturer"
                label={t('siteSettings.solar.controllerManufacturer', 'Lighting Controller Manufacturer')}
              />
              <SettingsTextField
                name="solar.lighting_controller_model_number"
                label={t('siteSettings.solar.controllerModel', 'Lighting Controller Model #')}
              />
              <SettingsTextField
                name="solar.lighting_controller_serial_number"
                label={t('siteSettings.solar.controllerSerial', 'Lighting Controller Serial #')}
              />
              <SettingsTextField
                name="solar.number_of_relays_in_controller"
                label={t('siteSettings.solar.relaysInController', 'Relays in Controller')}
                type="number"
              />
              <SettingsTextField
                name="solar.meter_number"
                label={t('siteSettings.solar.solarMeterNumber', 'Solar Meter Number')}
              />
            </SettingsFieldGroup>
          </Stack>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.utility', 'Utility & Permitting')} bordered>
          <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
            <SettingsTextField
              name="solar.utility_electrical_permit_number"
              label={t('siteSettings.solar.electricalPermit', 'Electrical Permit #')}
            />
            <SettingsTextField
              name="solar.utility_building_permit_number"
              label={t('siteSettings.solar.buildingPermit', 'Building Permit #')}
            />
            <SettingsTextField name="solar.utility_county" label={t('siteSettings.solar.county', 'Utility County')} />
            <SettingsTextField name="solar.utility_city" label={t('siteSettings.solar.city', 'Utility City')} />
            <SettingsTextField name="solar.utility_state" label={t('siteSettings.solar.state', 'Utility State')} />
            <SettingsTextField
              name="solar.date_electric_permit_granted"
              label={t('siteSettings.solar.electricalPermitDate', 'Electrical Permit Granted Date')}
            />
            <SettingsTextField
              name="solar.date_building_permit_granted"
              label={t('siteSettings.solar.buildingPermitDate', 'Building Permit Granted Date')}
            />
          </SettingsFieldGroup>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.contractor', 'Contractor Information')} bordered>
          <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
            <SettingsTextField
              name="solar.array_assembled_by"
              label={t('siteSettings.solar.arrayAssembledBy', 'Array Assembled By')}
            />
            <SettingsTextField
              name="solar.lighting_installed_by"
              label={t('siteSettings.solar.lightingInstalledBy', 'Lighting Installed By')}
            />
            <SettingsTextField
              name="solar.solar_installed_by"
              label={t('siteSettings.solar.solarInstalledBy', 'Solar Installed By')}
            />
            <SettingsTextField
              name="solar.electrical_solar_pv_installed_by"
              label={t('siteSettings.solar.electricalInstalledBy', 'Electrical PV Installed By')}
            />
          </SettingsFieldGroup>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.milestones', 'Milestone Dates')} bordered>
          <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
            <SettingsTextField
              name="solar.lighting_completion_date"
              label={t('siteSettings.solar.lightingCompletion', 'Lighting Completion Date')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.mechanical_solar_completion_date"
              label={t('siteSettings.solar.mechanicalCompletion', 'Mechanical Solar Completion Date')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.electrical_completion_date"
              label={t('siteSettings.solar.electricalCompletion', 'Electrical Completion Date')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.interconnection_agreement_filed"
              label={t('siteSettings.solar.interconnectionFiled', 'Interconnection Agreement Filed')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.interconnection_agreement_approved"
              label={t('siteSettings.solar.interconnectionApproved', 'Interconnection Agreement Approved')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.final_electrical_inspection_date"
              label={t('siteSettings.solar.finalInspection', 'Final Electrical Inspection')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <SettingsTextField
              name="solar.in_service_date"
              label={t('siteSettings.solar.inServiceDate', 'In Service Date')}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </SettingsFieldGroup>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.pop', 'Proof of Performance')} bordered>
          <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
            <SettingsTextField
              name="solar.lighting_photo_number"
              label={t('siteSettings.solar.lightingPhoto', 'Lighting Photo Number')}
            />
            <SettingsTextField
              name="solar.completed_install_photo_number"
              label={t('siteSettings.solar.completedPhoto', 'Completed Install Photo Number')}
            />
          </SettingsFieldGroup>
        </SettingsSubsection>

        <SettingsSubsection title={t('siteSettings.solar.costs', 'Costs')} bordered>
          <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
            <SettingsTextField
              name="solar.array_assembly_labor"
              label={t('siteSettings.solar.arrayLabor', 'Array Assembly Labor')}
            />
            <SettingsTextField
              name="solar.lighting_install_cost"
              label={t('siteSettings.solar.lightingInstallCost', 'Lighting Install Cost')}
            />
            <SettingsTextField
              name="solar.solar_install_cost"
              label={t('siteSettings.solar.solarInstallCost', 'Solar Install Cost')}
            />
            <SettingsTextField
              name="solar.lighting_controller_install_cost"
              label={t('siteSettings.solar.controllerInstallCost', 'Lighting Controller Install Cost')}
            />
            <SettingsTextField
              name="solar.solar_electrical_cost"
              label={t('siteSettings.solar.electricalCost', 'Solar Electrical Cost')}
            />
            <SettingsTextField
              name="solar.solar_mechanical_cost"
              label={t('siteSettings.solar.mechanicalCost', 'Solar Mechanical Cost')}
            />
            <SettingsTextField
              name="solar.lighting_cost"
              label={t('siteSettings.solar.lightingCost', 'Lighting Cost')}
            />
            <SettingsTextField
              name="solar.lighting_controller_cost"
              label={t('siteSettings.solar.controllerCost', 'Lighting Controller Cost')}
            />
            <SettingsTextField
              name="solar.additional_permitting_expense"
              label={t('siteSettings.solar.permittingExpense', 'Additional Permitting Expense')}
            />
            <SettingsTextField
              name="solar.design_power_consumption"
              label={t('siteSettings.solar.powerConsumption', 'Design Power Consumption')}
            />
            <SettingsTextField
              name="solar.design_power_generation"
              label={t('siteSettings.solar.powerGeneration', 'Design Power Generation')}
            />
          </SettingsFieldGroup>
        </SettingsSubsection>
      </Stack>
    </SettingsSection>
  );
}
